/**
 * Comprehensive Phone Number Validation and Normalization System
 * Handles international phone numbers with proper formatting and validation
 */

import { parsePhoneNumber, isValidPhoneNumber, formatPhoneNumber } from 'libphonenumber-js';

// Supported countries and their configurations
export const SUPPORTED_COUNTRIES = {
    IN: {
        name: 'India',
        code: '+91',
        flag: '🇮🇳',
        format: 'E164',
        minLength: 10,
        maxLength: 10
    },
    US: {
        name: 'United States',
        code: '+1',
        flag: '🇺🇸',
        format: 'E164',
        minLength: 10,
        maxLength: 10
    },
    GB: {
        name: 'United Kingdom',
        code: '+44',
        flag: '🇬🇧',
        format: 'E164',
        minLength: 10,
        maxLength: 11
    },
    AU: {
        name: 'Australia',
        code: '+61',
        flag: '🇦🇺',
        format: 'E164',
        minLength: 9,
        maxLength: 9
    },
    CA: {
        name: 'Canada',
        code: '+1',
        flag: '🇨🇦',
        format: 'E164',
        minLength: 10,
        maxLength: 10
    }
};

// Default country for parsing
export const DEFAULT_COUNTRY = 'IN';

// Phone number validation result
export class PhoneValidationResult {
    constructor(isValid, normalizedNumber, country, error, metadata = {}) {
        this.isValid = isValid;
        this.normalizedNumber = normalizedNumber;
        this.country = country;
        this.error = error;
        this.metadata = metadata;
    }

    getFormattedNumber(format = 'E164') {
        if (!this.isValid) return null;

        try {
            const parsed = parsePhoneNumber(this.normalizedNumber);
            return parsed.format(format);
        } catch (error) {
            return this.normalizedNumber;
        }
    }

    getDisplayNumber() {
        if (!this.isValid) return null;

        try {
            const parsed = parsePhoneNumber(this.normalizedNumber);
            return parsed.formatInternational();
        } catch (error) {
            return this.normalizedNumber;
        }
    }

    getNationalNumber() {
        if (!this.isValid) return null;

        try {
            const parsed = parsePhoneNumber(this.normalizedNumber);
            return parsed.formatNational();
        } catch (error) {
            return this.normalizedNumber;
        }
    }
}

/**
 * Validate and normalize a phone number
 * @param {string} phoneNumber - The phone number to validate
 * @param {string} defaultCountry - Default country code (default: 'IN')
 * @returns {PhoneValidationResult} - Validation result with metadata
 */
export const validateAndNormalizePhone = (phoneNumber, defaultCountry = DEFAULT_COUNTRY) => {
    if (!phoneNumber || typeof phoneNumber !== 'string') {
        return new PhoneValidationResult(
            false,
            null,
            null,
            'Phone number is required and must be a string', { input: phoneNumber }
        );
    }

    // Clean the input
    const cleanedNumber = phoneNumber.trim();

    if (!cleanedNumber) {
        return new PhoneValidationResult(
            false,
            null,
            null,
            'Phone number cannot be empty', { input: phoneNumber }
        );
    }

    try {
        // Try to parse with libphonenumber-js first
        const parsed = parsePhoneNumber(cleanedNumber, defaultCountry);

        if (!parsed || !isValidPhoneNumber(parsed.number, defaultCountry)) {
        // Fallback validation
        return validateWithFallback(cleanedNumber, defaultCountry);
        }

        const normalizedNumber = parsed.number;
        const country = parsed.country;

        // Additional validation
        const validationResult = performAdditionalValidation(parsed, defaultCountry);

        if (!validationResult.isValid) {
            return new PhoneValidationResult(
                false,
                null,
                country,
                validationResult.error, {
                    input: phoneNumber,
                    cleaned: cleanedNumber,
                    country: country,
                    type: parsed.getType(),
                    possible: parsed.isPossible(),
                    valid: parsed.isValid()
                }
            );
        }

        return new PhoneValidationResult(
            true,
            normalizedNumber,
            country,
            null, {
                input: phoneNumber,
                cleaned: cleanedNumber,
                country: country,
                type: parsed.getType(),
                possible: parsed.isPossible(),
                valid: parsed.isValid(),
                nationalNumber: parsed.nationalNumber,
                countryCallingCode: parsed.countryCallingCode
            }
        );

    } catch (error) {
        // Fallback validation
        return validateWithFallback(cleanedNumber, defaultCountry);
    }
};

/**
 * Validate using fallback method
 * @param {string} phoneNumber - The phone number to validate
 * @param {string} defaultCountry - Default country code
 * @returns {PhoneValidationResult} - Validation result
 */
const validateWithFallback = (phoneNumber, defaultCountry) => {
    try {
        // Basic validation - check if it's a valid phone number format
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        
        if (!phoneRegex.test(phoneNumber)) {
            return new PhoneValidationResult(
                false,
                null,
                null,
                'Invalid phone number format', { input: phoneNumber, library: 'fallback' }
            );
        }

        // Basic normalization - ensure it starts with +
        let normalized = phoneNumber;
        if (!normalized.startsWith('+')) {
            normalized = '+' + normalized;
        }
        
        return new PhoneValidationResult(
            true,
            normalized,
            defaultCountry,
            null, {
                input: phoneNumber,
                country: defaultCountry,
                library: 'fallback',
                normalized: normalized
            }
        );

    } catch (error) {
        return new PhoneValidationResult(
            false,
            null,
            null,
            `Phone number validation failed: ${error.message}`, { input: phoneNumber, error: error.message, library: 'fallback' }
        );
    }
};

/**
 * Perform additional validation checks
 * @param {Object} parsed - Parsed phone number object
 * @param {string} defaultCountry - Default country code
 * @returns {Object} - Validation result
 */
const performAdditionalValidation = (parsed, defaultCountry) => {
    // Check if number is possible
    if (!parsed.isPossible()) {
        return {
            isValid: false,
            error: 'Phone number is not possible for the given country'
        };
    }

    // Check if number is valid
    if (!parsed.isValid()) {
        return {
            isValid: false,
            error: 'Phone number is not valid'
        };
    }

    // Check number type (optional - can be customized based on requirements)
    const numberType = parsed.getType();
    const allowedTypes = ['MOBILE', 'FIXED_LINE', 'FIXED_LINE_OR_MOBILE'];

    if (!allowedTypes.includes(numberType)) {
        return {
            isValid: false,
            error: `Phone number type '${numberType}' is not allowed`
        };
    }

    // Check country-specific rules
    const countryValidation = validateCountrySpecificRules(parsed, defaultCountry);
    if (!countryValidation.isValid) {
        return countryValidation;
    }

    return { isValid: true, error: null };
};

/**
 * Validate country-specific rules
 * @param {Object} parsed - Parsed phone number object
 * @param {string} defaultCountry - Default country code
 * @returns {Object} - Validation result
 */
const validateCountrySpecificRules = (parsed, defaultCountry) => {
    const country = parsed.country;
    const nationalNumber = parsed.nationalNumber;

    // India-specific validation
    if (country === 'IN' || defaultCountry === 'IN') {
        // Indian mobile numbers should be 10 digits
        if (nationalNumber.length !== 10) {
            return {
                isValid: false,
                error: 'Indian mobile numbers must be 10 digits'
            };
        }

        // Check for valid Indian mobile number patterns
        const validPatterns = [
            /^[6-9]\d{9}$/, // Mobile numbers starting with 6, 7, 8, or 9
        ];

        const isValidPattern = validPatterns.some(pattern => pattern.test(nationalNumber));
        if (!isValidPattern) {
            return {
                isValid: false,
                error: 'Invalid Indian mobile number format'
            };
        }
    }

    // US/Canada-specific validation
    if (country === 'US' || country === 'CA' || defaultCountry === 'US' || defaultCountry === 'CA') {
        // North American numbers should be 10 digits
        if (nationalNumber.length !== 10) {
            return {
                isValid: false,
                error: 'North American phone numbers must be 10 digits'
            };
        }
    }

    return { isValid: true, error: null };
};

/**
 * Format phone number for display
 * @param {string} phoneNumber - Phone number to format
 * @param {string} format - Format type ('E164', 'INTERNATIONAL', 'NATIONAL')
 * @param {string} defaultCountry - Default country code
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumberForDisplay = (phoneNumber, format = 'INTERNATIONAL', defaultCountry = DEFAULT_COUNTRY) => {
    const validation = validateAndNormalizePhone(phoneNumber, defaultCountry);

    if (!validation.isValid) {
        return phoneNumber; // Return original if validation fails
    }

    try {
        const parsed = parsePhoneNumber(validation.normalizedNumber);

        switch (format.toUpperCase()) {
            case 'E164':
                return parsed.format('E164');
            case 'INTERNATIONAL':
                return parsed.formatInternational();
            case 'NATIONAL':
                return parsed.formatNational();
            default:
                return parsed.formatInternational();
        }
    } catch (error) {
        return validation.normalizedNumber;
    }
};

/**
 * Extract country code from phone number
 * @param {string} phoneNumber - Phone number to extract country code from
 * @returns {string|null} - Country code or null if not found
 */
export const extractCountryCode = (phoneNumber) => {
    const validation = validateAndNormalizePhone(phoneNumber);

    if (!validation.isValid) {
        return null;
    }

    return validation.country;
};

/**
 * Check if phone number is from a specific country
 * @param {string} phoneNumber - Phone number to check
 * @param {string} countryCode - Country code to check against
 * @returns {boolean} - True if phone number is from the specified country
 */
export const isPhoneNumberFromCountry = (phoneNumber, countryCode) => {
    const validation = validateAndNormalizePhone(phoneNumber);

    if (!validation.isValid) {
        return false;
    }

    return validation.country === countryCode;
};

/**
 * Get phone number metadata
 * @param {string} phoneNumber - Phone number to get metadata for
 * @returns {Object|null} - Metadata object or null if invalid
 */
export const getPhoneNumberMetadata = (phoneNumber) => {
    const validation = validateAndNormalizePhone(phoneNumber);

    if (!validation.isValid) {
        return null;
    }

    return validation.metadata;
};

/**
 * Validate phone number for specific use cases
 * @param {string} phoneNumber - Phone number to validate
 * @param {Object} options - Validation options
 * @returns {PhoneValidationResult} - Validation result
 */
export const validatePhoneForUseCase = (phoneNumber, options = {}) => {
    const {
        allowedCountries = [],
            requiredType = null,
            minLength = null,
            maxLength = null,
            defaultCountry = DEFAULT_COUNTRY
    } = options;

    const validation = validateAndNormalizePhone(phoneNumber, defaultCountry);

    if (!validation.isValid) {
        return validation;
    }

    // Check allowed countries
    if (allowedCountries.length > 0 && !allowedCountries.includes(validation.country)) {
        return new PhoneValidationResult(
            false,
            null,
            validation.country,
            `Phone number must be from one of: ${allowedCountries.join(', ')}`,
            validation.metadata
        );
    }

    // Check required type
    if (requiredType && validation.metadata.type !== requiredType) {
        return new PhoneValidationResult(
            false,
            null,
            validation.country,
            `Phone number must be of type: ${requiredType}`,
            validation.metadata
        );
    }

    // Check length constraints
    if (minLength && validation.metadata.nationalNumber.length < minLength) {
        return new PhoneValidationResult(
            false,
            null,
            validation.country,
            `Phone number must be at least ${minLength} digits`,
            validation.metadata
        );
    }

    if (maxLength && validation.metadata.nationalNumber.length > maxLength) {
        return new PhoneValidationResult(
            false,
            null,
            validation.country,
            `Phone number must not exceed ${maxLength} digits`,
            validation.metadata
        );
    }

    return validation;
};

/**
 * Batch validate multiple phone numbers
 * @param {Array} phoneNumbers - Array of phone numbers to validate
 * @param {string} defaultCountry - Default country code
 * @returns {Array} - Array of validation results
 */
export const batchValidatePhoneNumbers = (phoneNumbers, defaultCountry = DEFAULT_COUNTRY) => {
    if (!Array.isArray(phoneNumbers)) {
        throw new Error('Phone numbers must be an array');
    }

    return phoneNumbers.map(phoneNumber =>
        validateAndNormalizePhone(phoneNumber, defaultCountry)
    );
};

/**
 * Get supported countries list
 * @returns {Array} - Array of supported country objects
 */
export const getSupportedCountries = () => {
    return Object.entries(SUPPORTED_COUNTRIES).map(([code, config]) => ({
        code,
        ...config
    }));
};

/**
 * Check if country is supported
 * @param {string} countryCode - Country code to check
 * @returns {boolean} - True if country is supported
 */
export const isCountrySupported = (countryCode) => {
    return countryCode in SUPPORTED_COUNTRIES;
};

// Export default validation function
export default validateAndNormalizePhone;